<template>
  <div>
    <div class="position-relative mb-space-sm">
      <img
        :show="!bgImageLazy"
        :src="bgImageSource"
        class="vca-image vca-lazy" />
      <div class="play-btn-div">
        <button class="btn" @click="handleModalOpen($event)">
          <img :data-src="imgUrl" class="play-btn vca-lazy" />
        </button>
      </div>
    </div>
    <div
      v-if="displayCaption && caption"
      :class="captionClass"
      class="mb-space-sm"
      v-html="caption"></div>
    <div
      v-if="displayHighlight"
      :style="highlightStyle"
      :class="`bg-${props.highlightVariant}`"
      class="w-100"></div>

    <!-- video modal -->
    <rds-modal
      :visible="showModal"
      centered
      exterior-close-btn
      size="xl"
      body-class="p-0"
      @hidden="handleModalClose($event)"
      bg-variant="transparent">
      <div class="ratio ratio-16x9">
        <iframe
          title="Video"
          class="w-100 h-100"
          :src="videoEmbedUrl"
          ref="video"></iframe>
      </div>
    </rds-modal>
  </div>
</template>

<script setup lang="ts">
import { RdsModal } from "@rds-vue-ui/rds-modal";
import lozad from "lozad";
import { computed, onMounted, ref } from "vue";
import imgUrl from "./assets/play-button.svg?url";

interface Props {
  videoId: string;
  autoplayVideo?: boolean;
  bgImageSource: string;
  bgImageLazy?: boolean;
  displayCaption?: boolean;
  caption?: string;
  captionSize?: "small" | "medium" | "large" | "xl";
  captionVariant?: string;
  displayHighlight?: boolean;
  highlightHeight?: number;
  highlightVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  autoplayVideo: false,
  bgImageLazy: true,
  displayCaption: true,
  captionSize: "small",
  captionVariant: "dark-1",
  displayHighlight: true,
  highlightHeight: 4,
  highlightVariant: "secondary",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when the modal is closed.
   */
  (e: "modalClose", event: Event): void;
  /**
   *  Emitted when the modal is opened.
   */
  (e: "modalOpen", event: Event): void;
}>();

const showModal = ref(false);
const video = ref<HTMLVideoElement | null>();
const closedOnce = ref(false);

const videoEmbedUrl = computed(() => {
  const videoSource = `https://www.youtube.com/embed/${props.videoId}`;

  props.autoplayVideo && closedOnce.value ? `${videoSource}?&autoplay=1` : null;

  return videoSource;
});

const captionClass = computed(() => {
  return `fs-${props.captionSize} text-${props.captionVariant}`;
});

const highlightStyle = computed(() => {
  if (props.displayHighlight) {
    return `height: ${props.highlightHeight}px;`;
  }
  return "";
});

const handleModalOpen = (event: Event) => {
  emits("modalOpen", event);
  showModal.value = true;
};

const handleModalClose = (event: Event) => {
  emits("modalClose", event);
  closedOnce.value = true;
  video.value!.src = video.value!.src;
  showModal.value = false;
};

onMounted(() => {
  const observer = lozad(".vca-lazy");
  observer.observe();
});
</script>

<style lang="scss" scoped>
.vca-image {
  height: 200px;
  width: 100%;
  object-fit: cover;
  object-position: top;
}
.play-btn-div {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.video-close {
  position: absolute;
  display: block;
  right: 0;
  top: -15%;
}
.play-btn {
  height: 96px;
  width: 96px;
  opacity: 0.7;

  &:hover {
    transform: scale(1.05);
    opacity: 1;
    transition: transform 0.1s ease-out;
  }
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .vca-image {
    height: 500px;
  }
  .video-close {
    top: 0;
    right: -10%;
  }
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .vca-image {
    height: 550px;
  }
  .video-close {
    right: -5%;
  }
  .play-btn {
    height: 128px;
    width: 128px;
  }
}
</style>
